import type { TimelineSubmissionError } from './slice'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kActiveTimelineUpdateStatuses, TimelineAssetOverview, TimelineUpdateStatus } from './schemas'

/**
 * Select: asset overview for a matter.
 * - `undefined` - not fetched yet
 * - `null` - fetched; the matter has no timeline (empty state)
 * - `TimelineAssetOverview` - the living timeline
 */
export const selectTimelineAssetForMatter = createSelector(
  [(state: RootState) => state.timelineAssetState.assetByMatterId, (_: RootState, props: { matterId: string | undefined }) => props.matterId],
  (assetByMatterId, matterId): TimelineAssetOverview | null | undefined => {
    if (!matterId) return undefined
    return matterId in assetByMatterId ? assetByMatterId[matterId] : undefined
  }
)

export const selectTimelineAssetError = (state: RootState, props: { matterId: string | undefined }): boolean =>
  props.matterId ? state.timelineAssetState.assetErrorByMatterId[props.matterId] ?? false : false

/** Files roster, or null when not loaded. */
export const selectTimelineAssetFiles = (state: RootState, props: { assetId: string | undefined }) =>
  props.assetId ? state.timelineAssetState.filesByAssetId[props.assetId] ?? null : null

/** Latest polled update status (may be a terminal DONE/FAILED_FILES/ERROR). */
export const selectUpdateStatusForAsset = (state: RootState, props: { assetId: string | undefined }) =>
  props.assetId ? state.timelineAssetState.updateStatusByAssetId[props.assetId] ?? null : null

/** Full update history for an asset (newest-first), or null when not loaded. */
export const selectUpdateHistoryForAsset = (state: RootState, props: { assetId: string | undefined }) =>
  props.assetId ? state.timelineAssetState.updateHistoryByAssetId[props.assetId] ?? null : null

/**
 * Select: whether an update run is actively processing (drives polling banners/skeletons).
 * FAILED_FILES is deliberately excluded - it is a resting state awaiting a user decision.
 */
export const selectIsUpdateActive = createSelector(
  [
    (state: RootState, props: { matterId: string | undefined }) => selectTimelineAssetForMatter(state, props),
    (state: RootState) => state.timelineAssetState.updateStatusByAssetId,
  ],
  (asset, updateStatusByAssetId): boolean => {
    if (!asset) return false
    const polled = updateStatusByAssetId[asset.id]
    // Prefer the freshest polled status for the same run; fall back to the overview's active_update
    if (polled && (!asset.active_update || polled.update.id === asset.active_update.id)) {
      return kActiveTimelineUpdateStatuses.includes(polled.update.status)
    }
    if (asset.active_update) {
      return kActiveTimelineUpdateStatuses.includes(asset.active_update.status)
    }
    return false
  }
)

/** The unresolved FAILED_FILES run for an asset, if any (from poll status or the overview). */
export const selectFailedFilesUpdate = createSelector(
  [
    (state: RootState, props: { matterId: string | undefined }) => selectTimelineAssetForMatter(state, props),
    (state: RootState) => state.timelineAssetState.updateStatusByAssetId,
  ],
  (asset, updateStatusByAssetId) => {
    if (!asset) return null
    const polled = updateStatusByAssetId[asset.id]
    if (polled && polled.update.status === TimelineUpdateStatus.FAILED_FILES) return polled
    if (asset.active_update?.status === TimelineUpdateStatus.FAILED_FILES) {
      // No per-file detail until the one-shot status fetch lands; expose the update alone
      return { update: asset.active_update, progress: null, failed_files: null }
    }
    return null
  }
)

export const selectDataErrorForAsset = (state: RootState, props: { assetId: string | undefined }): boolean =>
  props.assetId ? state.timelineAssetState.dataErrorByAssetId[props.assetId] ?? false : false

export const selectInitializePending = (state: RootState, props: { matterId: string | undefined }): boolean =>
  props.matterId ? state.timelineAssetState.initializePendingByMatterId[props.matterId] ?? false : false

export const selectInitializeError = (state: RootState, props: { matterId: string | undefined }): TimelineSubmissionError | null =>
  props.matterId ? state.timelineAssetState.initializeErrorByMatterId[props.matterId] ?? null : null

export const selectMutationPending = (state: RootState, props: { assetId: string | undefined }): boolean =>
  props.assetId ? state.timelineAssetState.mutationPendingByAssetId[props.assetId] ?? false : false

export const selectMutationError = (state: RootState, props: { assetId: string | undefined }): TimelineSubmissionError | null =>
  props.assetId ? state.timelineAssetState.mutationErrorByAssetId[props.assetId] ?? null : null

/**
 * Select: counts of the matter's in-progress Add Files staging session — uploads not yet
 * initialized (pending) plus staged files. Zero counts when the staging belongs to another
 * matter. Drives the blank-state "continue in Add Files" notice.
 */
export const selectAddFilesStagingCountsForMatter = createSelector(
  [(state: RootState) => state.timelineAssetState.addFilesStaging, (_: RootState, props: { matterId: string | undefined }) => props.matterId],
  (staging, matterId): { pendingCount: number; stagedCount: number } => {
    if (!matterId || staging.matterId !== matterId) return { pendingCount: 0, stagedCount: 0 }
    return { pendingCount: staging.pendingUploadClientIds.length, stagedCount: staging.stagedUserFileIds.length }
  }
)
